import React, { useState, useContext } from "react";
import { GlobalContext } from "./GlobalContext";
import { DropdownMenu, Avatar } from "@radix-ui/themes";
import { Search } from "lucide-react";
import UserAccountModal from "./AddEditUserAccount";
import ChangePasswordModal from "./ChangePassword";
import SecurityPasskeysModal from "./SecurityPasskeys";
import { NotificationsModal } from "./NotificationSettings";
import NotificationBell from "./NotificationBell";
import UnsentPayments from "./UnsentPayments";
import HelpMenu from "./HelpMenu";
import GlobalSearch from "./components/GlobalSearch";
import { useIsMobile } from "./hooks/useMediaQuery";
import { apiFetch } from './api.js';

function CarsHead() {
    const globals = useContext(GlobalContext);
    const { toggleMessageBox, logOut } = globals;
    const user = globals.userData || {};
    const companyName = globals?.companyData?.CompanyName || "Loading...";
    // Dealerships without their own logo fall back to the Auto Dealer UG mark;
    // logo-mark.png is the emblem sized for this 40-60px slot (logo512 was a
    // 512px PWA icon being downscaled by the browser on every load).
    const LogoURL = globals?.companyData?.LogoURL || "/logo-mark.png";
    console.log('Company Data in CarsHead:', globals.companyData);
    const { FirstName, LastName } = globals.userData || {};
    console.log('User Data in CarsHead:', globals.userData);
    const initials = FirstName ? `${FirstName.charAt(0)}${LastName.charAt(0)}` : "";
    const [modalVisible, setModalVisibility] = React.useState({
        visible: false, mode: "edit"
    });
    const [changePasswordVisible, setChangePasswordVisibility] = React.useState(false);
    const [securityVisible, setSecurityVisibility] = React.useState(false);
    const [notificationsVisible, setNotificationsVisibility] = React.useState(false);
    const [profilePic, setProfilePic] = useState(user?.profilePic || "");
    //Phones have no room for an inline search box beside the company name, so
    //it opens as an overlay across the header row instead. Gate on the hook
    //rather than CSS alone: a `hidden md:block` desktop copy would still mount,
    //and both copies would register the Ctrl+K / "/" shortcut — leaving the
    //shortcut focusing an invisible input on phones.
    const isMobile = useIsMobile();
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

    const handleLogOut = async () => {
        try {
            const response = await apiFetch('/logout');

            if (response.headers?.get('Content-Type')?.includes('application/json')) {
                const resObj = await response.json();

                if (resObj.ok) {
                    logOut();
                } else {
                    console.error("Logout failed:", resObj.message);
                    toggleMessageBox({
                        on: true,
                        title: "Error",
                        message: resObj.message,
                        buttons: ["OK"]
                    });
                }
            } else {
                const responseText = await response.text();
                toggleMessageBox({
                    on: true,
                    title: "Error",
                    message: responseText,
                    buttons: ["OK"]
                });
                console.error("Unexpected response format:", responseText);
            }
        } catch (err) {
            toggleMessageBox({
                on: true,
                title: "Error",
                message: "An error occurred while logging out. Please try again." + err.message,
                buttons: ["OK"]
            });
            console.error("Logout failed:", err);
        }
    }

    return (
        // The bottom hairline takes the tenant accent (softened): with the tab
        // strip now neutral, this is what ties the chrome to the dealership's
        // theme without going back to a saturated band.
        <div className="relative bg-gray-100 flex items-center px-3 py-2 md:px-6 md:py-3 shadow-md sticky top-0 z-50 border-b border-[var(--theme-accent-border)]">
            {/* Logo */}
            {/* Not a button. It used to start the app overview on click, which
                nothing announced and nobody found; the tours are in the help
                menu below now. */}
            <div
                className="w-10 h-10 md:w-[60px] md:h-[60px] shrink-0 bg-no-repeat bg-contain bg-center mr-3 md:mr-4 rounded-full"
                style={{ backgroundImage: `url(https://app.autodealerug.com${LogoURL})` }}
                role="img"
                aria-label={`${companyName} logo`}
            />
            
            {/* Company Name */}
            <div className="flex-grow min-w-0 truncate text-gray-800 text-lg md:text-2xl font-bold tracking-wide">
                {companyName}
            </div>

            {/* Universal vehicle search. Fixed width rather than flex-1 so it
                doesn't fight the truncating company name as the window resizes. */}
            {!isMobile && (
                //id on the desktop wrapper, not inside GlobalSearch: the mobile
                //drawer renders a second copy of the component, and the app
                //overview tour needs a target that resolves exactly once.
                <div id="global-search" className="shrink-0 w-64 lg:w-80 xl:w-96 mx-4">
                    <GlobalSearch />
                </div>
            )}

            {/* Header Actions */}
            <div className="flex items-center gap-2 md:gap-4 shrink-0">
                {/* Search (mobile) — expands to the overlay below */}
                {isMobile && <button
                    type="button"
                    className="p-2 rounded-full text-gray-600 hover:bg-gray-200 transition-colors"
                    aria-label="Search vehicles"
                    onClick={() => setMobileSearchOpen(true)}
                >
                    <Search className="h-5 w-5" />
                </button>}

                {/* Help and tours. Desktop only — it returns null on mobile,
                    where the tours have nothing to point at. */}
                <HelpMenu onOpenSecurity={() => setSecurityVisibility(true)} />

                {/* Payments kept on this device because they could not be sent.
                    Renders nothing when there are none, so it costs no space in
                    the normal case. */}
                <UnsentPayments />

                {/* Notification Bell */}
                <NotificationBell />

                {/* User Profile Dropdown */}
                <div className="flex items-center justify-center md:min-w-[100px]" id="profile-button">
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                            <button 
                                className="bg-transparent border-none p-0 cursor-pointer block m-auto transition-transform hover:scale-105 active:scale-95 focus:outline-none"
                            >
                                <div className="ring-2 ring-[var(--theme-accent)] ring-offset-2 ring-offset-transparent rounded-full hover:ring-[var(--theme-accent-hover)] transition-all">
                                    <Avatar
                                        size="3"
                                        src={profilePic}
                                        radius="full"
                                        fallback={initials}
                                    />
                                </div>
                            </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content className="bg-white rounded-lg shadow-xl border border-gray-200 p-1 min-w-[180px]">
                            <DropdownMenu.Item 
                                className="px-3 py-2 text-sm text-gray-700 hover:bg-[var(--theme-accent-tint)] hover:text-[var(--theme-accent-ink)] rounded cursor-pointer transition-colors"
                                onClick={() => { setModalVisibility({ visible: true, mode: "edit" }) }}
                            >
                                Edit Profile
                            </DropdownMenu.Item>
                            <DropdownMenu.Item 
                                className="px-3 py-2 text-sm text-gray-700 hover:bg-[var(--theme-accent-tint)] hover:text-[var(--theme-accent-ink)] rounded cursor-pointer transition-colors"
                                onClick={() => setChangePasswordVisibility(true)}
                            >
                                Change Password
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                                className="px-3 py-2 text-sm text-gray-700 hover:bg-[var(--theme-accent-tint)] hover:text-[var(--theme-accent-ink)] rounded cursor-pointer transition-colors"
                                onClick={() => setSecurityVisibility(true)}
                            >
                                Security &amp; passkeys
                            </DropdownMenu.Item>
                            {/*Personal, like the three above it. Moved out of
                               Settings, which is company configuration and gated
                               accordingly — your own inbox preferences are not
                               something you should need admin rights to reach.*/}
                            <DropdownMenu.Item
                                className="px-3 py-2 text-sm text-gray-700 hover:bg-[var(--theme-accent-tint)] hover:text-[var(--theme-accent-ink)] rounded cursor-pointer transition-colors"
                                onClick={() => setNotificationsVisibility(true)}
                            >
                                Notifications
                            </DropdownMenu.Item>
                            <DropdownMenu.Separator className="my-1 h-px bg-gray-200" />
                            <DropdownMenu.Item 
                                className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded cursor-pointer transition-colors font-medium"
                                onClick={handleLogOut}
                            >
                                Log Out
                            </DropdownMenu.Item>
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>
                </div>
            </div>

            {/* Mobile search overlay. Sits on top of the header row rather than
                pushing it, so the 56px height (--m-header-h) and every
                calc(100dvh - …) rule that depends on it stay untouched. */}
            {isMobile && mobileSearchOpen && (
                <div className="absolute inset-0 z-10 flex items-center gap-2 bg-gray-100 px-3">
                    <GlobalSearch
                        className="flex-1"
                        autoFocus
                        onDismiss={() => setMobileSearchOpen(false)}
                    />
                    <button
                        type="button"
                        className="shrink-0 text-sm font-medium text-[var(--theme-accent)] px-1"
                        onClick={() => setMobileSearchOpen(false)}
                    >
                        Cancel
                    </button>
                </div>
            )}

            {/* Modals */}
            {modalVisible.visible && 
                <UserAccountModal 
                    user={globals.userData || {}}
                    isOpen={modalVisible.visible}
                    mode={modalVisible.mode}
                    onClose={() => setModalVisibility({ visible: false })}
                />
            }
            {changePasswordVisible &&
                <ChangePasswordModal
                    isOpen={changePasswordVisible}
                    onClose={() => setChangePasswordVisibility(false)}
                />
            }
            {securityVisible &&
                <SecurityPasskeysModal
                    isOpen={securityVisible}
                    onClose={() => setSecurityVisibility(false)}
                />
            }
            {notificationsVisible &&
                <NotificationsModal
                    isOpen={notificationsVisible}
                    onClose={() => setNotificationsVisibility(false)}
                />
            }
        </div>
    )
}

export default CarsHead;